import { useCallback, useEffect, useMemo } from 'react';
import { getDeviceType, isIPadOS13Plus } from 'utils/deviceInfoUtils';

/**
 * A hook that provides browser navigation protection by showing a confirmation dialog
 * when the user attempts to navigate away from the current page (refresh, close tab, etc.)
 *
 * **Desktop behavior:**
 * - ✅ Shows prompt for all navigation: home button, reload, close tab
 *
 * **Mobile & Tablet behavior:**
 * - ❌ DISABLED ENTIRELY: beforeunload prompts are unreliable on mobile devices
 * - Home button: Prompt appears AFTER user has already left (orphaned dialog)
 * - Reload: Prompt may or may not appear depending on browser
 * - Close tab: Prompt may or may not appear depending on browser
 *
 * **Why disabled on mobile & tablets:**
 * After extensive testing and research, we found that:
 * 1. beforeunload is the ONLY event that can show prompts
 * 2. There is NO reliable way to detect home button vs reload vs close tab
 * 3. All attempted workarounds (visibilitychange, pagehide, delays) still show orphaned dialogs
 * 4. Mobile OS prioritizes instant home button response over web page dialogs
 * 5. This is intentional browser behavior for security/UX reasons
 * 6. Both phones AND tablets exhibit the same orphaned dialog behavior
 *
 * **Note on device detection:**
 * Uses user-agent based detection (not viewport) to distinguish between:
 * - Mobile OS devices (iOS, Android) - prompts disabled
 * - Desktop OS devices (Windows, macOS, Linux) - prompts enabled
 * This ensures Windows tablets/Surface devices get prompts while iOS/Android don't.
 *
 * **Recommended alternatives for mobile & tablet:**
 * - Implement auto-save to sessionStorage/server-side with PHI/PII consideration
 * - Show visual indicators when there's unsaved data
 *
 * @param enabled Whether to enable the navigation confirmation
 * @returns Object with suppressBeforeUnload callback for legacy support
 */
export const useNavigationConfirmation = (enabled: boolean) => {
  const isMobileOrTablet = useMemo(() => {
    const deviceType = getDeviceType();
    const isMobileOrTabletDevice =
      deviceType === 'iPhone' ||
      deviceType === 'android' ||
      deviceType === 'mobile' ||
      deviceType === 'tablet';
    return isMobileOrTabletDevice;
  }, []);

  // Handle browser close/refresh - shows native browser dialog
  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (!enabled || isMobileOrTablet) {
        return;
      }

      // Desktop only: show prompt
      e.preventDefault();
      e.returnValue = '';
    },
    [enabled, isMobileOrTablet],
  );

  const suppressBeforeUnload = useCallback(() => {
    // Suppress the beforeunload event by removing the listener
    window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [handleBeforeUnload]);

  // Register/unregister the event listener based on enabled state
  useEffect(() => {
    // Register on desktop only, skip on mobile phones and tablets
    if (enabled && !isMobileOrTablet) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, handleBeforeUnload, isMobileOrTablet]);

  return {
    suppressBeforeUnload,
  };
};
